/**
 * @format
 */
import AppPopup from '@/components/popups/app-popup';
import EventBus from '@/scripts/event-bus';
import BoardUtility from '@/scripts/board-utility';
import { FormService } from '@/scripts/services/form/form-service';
import { LogLevel } from '@/scripts/logger';

export const FormsBoardServiceMixin = {
    data() {
        return {
            selectedItems: [],
            deletingItems: [],
        };
    },

    computed: {
        baseUrl() {
            return this.$store.getters.baseUrl;
        },

        accountId() {
            return this.$store.state.context.account.id;
        },
    },

    methods: {
        refreshEvent() {
            EventBus.$emit('forms:update');
        },

        onLandingPageUsingIt({ id }) {
            this.$router.push(`${this.baseUrl}/LandingPages/Forms/${id}/Overview`);
        },

        onFormSumbmission({ id }) {
            this.$router.push(`${this.baseUrl}/LandingPages/Forms/${id}/Submissions`);
        },

        editItem({ id, clientId }) {
            const widget = {
                name: 'form-widget',
                clientId: clientId,
                formId: id,
            };

            EventBus.$emit('open-widget', widget);
        },

        createLandingPageForm({ id, clientId }) {
            const widget = {
                name: 'form-from-template-widget',
                accountId: this.accountId,
                clientId: clientId,
                formId: id,
            };

            EventBus.$emit('open-widget', widget);
        },

        addToMyForms({ id }) {
            FormService.addToMyForms(this.accountId, id)
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        "You've successfully added the form to My Forms.",
                    );
                    this.refreshEvent();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('There was an error adding the form.', {
                        error,
                    });
                });
        },

        bulkAutoPublish(items) {
            const ids = items.map(({ id }) => id);
            FormService.bulkAutoPublishForm(ids)
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        'Your Auto Publish settings have been updated.',
                    );
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'Your Auto Publish settings failed to update.',
                        { error },
                    );
                })
                .finally(() => {
                    this.selectedItems = [];
                    this.refreshEvent();
                });
        },

        onPublish(item) {
            if (!item.isAutoPublished) {
                const widget = {
                    name: 'landing-page-form-publish-widget',
                    landingPageFormId: item.id,
                };

                EventBus.$emit('open-widget', widget);
            } else {
                this.$helpers.showFailNotification('This template is already set to Auto Publish', {
                    level: LogLevel.INFO,
                });
            }
        },

        onUnpublish({ id }) {
            FormService.unpublish(id)
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        'Your publish settings have been updated.',
                    );
                    this.refreshEvent();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while saving your landing page form publish settings.',
                        { error },
                    );
                });
        },

        duplicateItem({ id, form: { label } }) {
            const widget = {
                name: 'duplicate-widget',
                entityId: id,
                entityName: label,
                serviceName: 'Forms',
                entityType: 'Form',
                fieldLabel: 'Choose a name for the new form',
                fieldPlaceholder: 'Form Name',
                replaceEntityField: 'Name',
                onSuccess: () => {
                    this.refreshEvent();
                },
            };

            EventBus.$emit('open-widget', widget);
        },

        deleteItem(item) {
            this.deletingItems = [item];

            const {
                id,
                form: { label },
            } = item;

            FormService.delete(id)
                .then(() => {
                    this.$helpers.showSuccessNotification(`Form: ${label}, deleted successfully`);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(error.response.data.Message, { error });
                })
                .finally(() => {
                    this.deletingItems = [];
                    this.refreshEvent();
                });
        },

        deleteItems(items) {
            this.deletingItems = [...items];

            const ids = this.deletingItems.map(({ id }) => id);

            FormService.deleteAll(ids)
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        `${
                            ids.length > 1 ? `${ids.length} forms have` : '1 form has'
                        } been deleted.`,
                    );
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(error.response.data.Message, { error });
                })
                .finally(() => {
                    this.selectedItems = [];
                    this.deletingItems = [];
                    this.refreshEvent();
                });
        },

        // Factories

        createDeleteFormModal(item, confirmFunction, contentCopy) {
            return () => {
                const actions = {
                    confirm: {
                        label: 'Delete Form',
                        action: () => confirmFunction(item),
                    },

                    cancel: {
                        label: 'Cancel',
                    },
                };

                const defaultContent =
                    '<p>All information associated with the form, including form submissions, will be deleted. You can not undo this action.</p>';

                this.$modal.show(
                    AppPopup,
                    {
                        type: 'alert-popup',
                        title: 'Are you sure you want to delete this form?',
                        content: contentCopy ? contentCopy : defaultContent,
                        actions,
                        confirmText: 'Delete',
                    },
                    {
                        height: this.$helpers.setPopupHeight(),
                        width: this.$helpers.setPopupWidth(),
                    },
                );
            };
        },

        createDeleteMultipleFormsModal(confirmFunction, contentCopy) {
            return () => {
                const actions = {
                    confirm: {
                        label: 'Delete Forms',
                        action: confirmFunction,
                    },

                    cancel: {
                        label: 'Cancel',
                    },
                };

                const defaultContent =
                    '<p>All information associated with these forms, including form submissions, will be deleted. You can not undo this action.</p>';

                this.$modal.show(
                    AppPopup,
                    {
                        type: 'text-confirm-popup',
                        title: 'Are you sure?',
                        content: contentCopy ? contentCopy : defaultContent,
                        actions,
                        confirmText: 'Delete',
                    },
                    {
                        height: this.$helpers.setPopupHeight(),
                        width: this.$helpers.setPopupWidth(),
                    },
                );
            };
        },

        // Table mapper

        asFormItem(item) {
            const isAgencyAvailable =
                item.Account && item.Account.Agency && item.Account.Agency.Name;
            return {
                id: item.Id,
                form: {
                    id: item.Id,
                    label: item.Name,
                    link: `Forms/${item.Id}/Submissions`,
                },
                formBuilder: {
                    id: item.Id,
                    label: item.Name,
                    link: `Forms/${item.Id}/Design`,
                },
                uniqueViews: +item.UniqueViews,
                entries: +item.Entries,
                agency: isAgencyAvailable
                    ? {
                          logoUrl: item.Account.Agency.LogoImageUrl,
                          name: item.Account.Agency.Name,
                      }
                    : null,
                publishedAt: this.$moment(item.PublishedAt),
                createdAt: this.$moment(item.CreatedAt),
                updatedAt: this.$moment(item.UpdatedAt),
                categories: item.Categories
                    ? item.Categories.map(({ Name }) => ({ name: Name }))
                    : [],
                title: item.Title,
                description: item.Description,
                config: item.Configuration,
                autoPublished: {
                    landingPageFormId: item.Id,
                    name: item.Name,
                    isAutoPublished: !!item.IsAutoPublished,
                },
                asset: {
                    landingPageFormId: item.Id,
                    isAutoPublished: !!item.IsAutoPublished,
                    published: item['SharedAssets@odata.count'],
                },
                plansPublishedTo: item['SharedAssets@odata.count'],
            };
        },

        // Query methods

        getFilterParam(options, type, clientId, promoId) {
            const queryFilters = [];

            if (options.filters) {
                const viewQueryFilter = this.getViewQueryFilter(options.filters);
                if (viewQueryFilter) {
                    queryFilters.push(viewQueryFilter);
                }
            }

            if (options.search) {
                queryFilters.push(this.getSearchFilter(options.search));
            }

            if (type === 'forms-shared') {
                queryFilters.push(`SharedAssets/any(a: a/AgencyClient/ClientId eq ${clientId})`);
            } else if (type === 'forms-subscription') {
                queryFilters.push(`SharedAssets/any(a: a/PromoId eq ${promoId})`);
            } else {
                queryFilters.push(`AccountId eq ${this.accountId}`);
            }

            if (type === 'forms-published') {
                queryFilters.push('SharedAssets/any(a: a/AgencyClient/IsAccepted eq true)');
            } else if (type === 'forms-organization-published') {
                queryFilters.push('SharedAssets/any()');
            } else if (type === 'forms-unpublished' || type === 'forms-organization-unpublished') {
                queryFilters.push('not SharedAssets/any()');
            }

            return queryFilters.length > 0 ? `${queryFilters.join(' and ')}` : null;
        },

        getViewQueryFilter(filters) {
            if (filters.value.length > 0) {
                const viewQueryFilters = filters.value.map(this.mapFilter);
                if (filters.operation === 'Or') {
                    return `(${viewQueryFilters.join(' or ')})`;
                } else {
                    return `(${viewQueryFilters.join(' and ')})`;
                }
            }

            return null;
        },

        mapFilter(filter) {
            switch (filter.key) {
                case 'form-name':
                    return BoardUtility.getFormNameFilterQuery(filter);
                default:
                    return null;
            }
        },

        getSearchFilter(search) {
            const encodedSearch = encodeURIComponent(search);

            return `(contains(Name, '${encodedSearch}'))`;
        },

        getPageParam(options) {
            return options.itemsPerPage && options.page
                ? `${options.itemsPerPage * (options.page - 1)}`
                : null;
        },

        getTopParam(options) {
            return options.itemsPerPage ? `${options.itemsPerPage}` : null;
        },

        getOrderParam(options) {
            return options.sort ? `${options.sort.property} ${options.sort.direction}` : null;
        },

        getExpandParam(type) {
            const expandParams = ['ListForms($select=ListId)', 'Categories($select=Name)'];
            if (type === 'forms-shared') {
                expandParams.push('Account($select=Id;$expand=Agency($select=Name,LogoImageUrl))');
            } else if (type === 'forms-published' || type === 'forms-organization-published') {
                expandParams.push('SharedAssets($count=true;$top=0)');
            }

            return expandParams.join(',');
        },

        createParamsFromBoardQuery(options, type, clientId, promoId) {
            return {
                filter: this.getFilterParam(options, type, clientId, promoId),
                skip: this.getPageParam(options),
                top: this.getTopParam(options),
                orderBy: this.getOrderParam(options),
                expand: this.getExpandParam(type),
                count: true,
            };
        },

        // Get Forms

        dispatchGetForms({ options = {}, type, clientId, promoId }, signal) {
            const requestConfig = this.createParamsFromBoardQuery(options, type, clientId, promoId);

            return new Promise((resolve, reject) => {
                FormService.getAll(requestConfig, signal)
                    .then(({ data }) => {
                        resolve({
                            items: data.value.map(this.asFormItem),
                            count: data['@odata.count'],
                        });
                    })
                    .catch(reject);
            });
        },

        getFormById(formId, options) {
            return new Promise((resolve, reject) => {
                FormService.get(formId, options)
                    .then(({ data }) => {
                        resolve({
                            item: data.value.map(this.asFormItem)[0] || null,
                        });
                    })
                    .catch(reject);
            });
        },
    },
};
